import React, { useEffect } from 'react';
import { useGridApiRef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import useSidePanel from 'talent_matching/hooks/use_side_panel';
import useFilterTableState from 'talent_matching/hooks/use_filter_table_state';
import { useColumnState } from 'talent_matching/hooks/use_column_state';
import useStickyGridHeader from 'talent_matching/hooks/use_sticky_grid_header';
import { useMyGreenhouseCandidates } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';
import { useMyGreenhouseColumns } from 'talent_matching/my_greenhouse/components/table/my_greenhouse_columns';
import { useMyGreenhouseTabContext } from 'talent_matching/contexts/my_greenhouse_tab_context';
import BaseDataTable from 'talent_matching/components/shared/base_data_table';
import NoOverflowFullWidth from 'talent_matching/components/shared/no_overflow_full_width';
import { TABS } from 'talent_matching/contexts/user_context';
import { Person } from 'talent_matching/types';
import { RowDef } from '@grnhse/seedling/lib/birch/components/table/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';

const t = withLocalePrefix('talent_matching.table.no_results');

type Props = {
  onTotalRowsChange?: (totalRows: number) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  hasAnyFilters?: boolean;
  onClearAll?: () => void;
};

const NoResultsOverlay = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
    <Body>{t('header')}</Body>
  </Stack>
);

const MyGreenhouseTableWrapper = ({
  onTotalRowsChange,
  columnVisibilityModel,
  setColumnVisibilityModel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasAnyFilters = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onClearAll,
}: Props) => {
  const { paths } = useMyGreenhouseTabContext();

  const {
    data: tableData,
    isLoading,
    isFetching,
    isPlaceholderData,
  } = useMyGreenhouseCandidates(paths.candidatesPath);

  const rows = tableData?.rows || [];
  const totalRows = tableData?.totalRows || 0;

  const { mountKey, currentPage, rowsPerPage, onPageChange } =
    useFilterTableState({ tabId: TABS.MY_GREENHOUSE, totalRows });

  useEffect(() => {
    onTotalRowsChange?.(totalRows);
  }, [totalRows, onTotalRowsChange]);

  const tableLoading = isLoading || isFetching || isPlaceholderData;
  const columns = useMyGreenhouseColumns();

  const apiRef = useGridApiRef();
  const { handleColumnChange } = useColumnState(apiRef, columnVisibilityModel);

  const { wrapperRef } = useStickyGridHeader();

  const { handleRowClick } = useSidePanel({ rows });

  const setRefs = (el: HTMLDivElement | null) => {
    wrapperRef.current = el;
  };

  return (
    <NoOverflowFullWidth>
      <div ref={setRefs}>
        <BaseDataTable
          key={mountKey}
          rows={rows}
          columns={columns}
          totalRows={totalRows}
          loading={tableLoading}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          apiRef={apiRef}
          onColumnWidthChange={handleColumnChange}
          slots={{
            noRowsOverlay: () => <NoResultsOverlay />,
          }}
          onRowClick={(row: RowDef) => handleRowClick(row as Person)}
        />
      </div>
    </NoOverflowFullWidth>
  );
};

export default MyGreenhouseTableWrapper;
